import type { Icd10DescriptionMap } from "@/lib/billing/icd10-descriptions";

export const ICD10_CODES_FIELD_NAME = "ICD-10 Codes";

export const isIcd10CodesQuestion = (question: {
  measurementFieldName?: string | null;
}): boolean => question.measurementFieldName === ICD10_CODES_FIELD_NAME;

/** The picked codes, plus the provider's optional comment on each, keyed by code. */
export interface Icd10Answer {
  codes: string[];
  comments: Record<string, string>;
}

export interface Icd10CodeLine {
  code: string;
  description?: string;
  comment?: string;
}

export const readIcd10Answer = (data: Record<string, any>): Icd10Answer => {
  const codes = Array.isArray(data.values)
    ? data.values.filter((v: unknown): v is string => typeof v === "string")
    : [];
  const raw =
    data.comments && typeof data.comments === "object" ? data.comments : {};
  const comments = Object.fromEntries(
    Object.entries(raw).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );
  return { codes, comments };
};

/**
 * Answer data for the note. `values` stays the bare code list the chart's
 * measurement history and superbills read; comments ride beside it and are
 * left out entirely when there are none, so a note without comments saves
 * exactly what it did before comments existed.
 */
export const toIcd10AnswerData = ({
  codes,
  comments,
}: Icd10Answer): Record<string, any> => {
  const kept = Object.fromEntries(
    codes
      .filter((code) => comments[code]?.trim())
      .map((code) => [code, comments[code]]),
  );
  return Object.keys(kept).length > 0
    ? { values: codes, comments: kept }
    : { values: codes };
};

export const icd10CodeLines = (
  data: Record<string, any>,
  descriptions: Icd10DescriptionMap = {},
): Icd10CodeLine[] => {
  const { codes, comments } = readIcd10Answer(data);
  return codes.map((code) => ({
    code,
    description: descriptions[code.toUpperCase()],
    comment: comments[code]?.trim() || undefined,
  }));
};
